import { useState } from 'react'
import { Folder, FolderX, Search } from 'lucide-react'
import { useBrowseDepartments } from '@/hooks/usePlanBrowse'
import { useDebounce } from '@/hooks/useDebounce'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FolderCard } from './FolderCard'
import { FolderGrid, FolderGridSkeleton } from './FolderGrid'
import { useDepartmentSelectionStore } from '@/stores/useDepartmentSelectionStore'
import type { BrowseDepartmentSelection } from '@/stores/useBrowseStore'

interface DepartmentGridProps {
  divisionId: string
  onSelect: (dept: BrowseDepartmentSelection) => void
}

export function DepartmentGrid({ divisionId, onSelect }: DepartmentGridProps) {
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)

  const { data, isLoading, isError, isFetching, refetch } =
    useBrowseDepartments(divisionId, debouncedSearch || undefined)
  const { selectedDepartments, toggleDepartment } =
    useDepartmentSelectionStore()

  const departments = data?.data ?? []

  return (
    <div className="flex flex-col gap-4">
      <div className="relative max-w-[352px]">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search departments..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="pl-9 bg-muted/50"
        />
      </div>

      {isLoading ? (
        <FolderGridSkeleton label="Loading departments" count={8} />
      ) : isError ? (
        <div role="alert" className="flex flex-col items-center gap-2 py-16">
          <p className="text-sm text-muted-foreground">
            Failed to load departments
          </p>
          <Button
            variant="outline"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </div>
      ) : departments.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
          <FolderX className="h-10 w-10" />
          <p className="text-sm">No departments found</p>
        </div>
      ) : (
        <FolderGrid>
          {departments.map(dept => (
            <FolderCard
              key={dept.id}
              icon={<Folder className="h-6 w-6" />}
              title={dept.name}
              subtitle={`${dept.plan_count} plan${dept.plan_count !== 1 ? 's' : ''}`}
              onClick={() =>
                onSelect({ id: dept.id, name: dept.name, code: dept.code })
              }
              selectable
              selected={selectedDepartments.has(dept.id)}
              onSelect={() =>
                toggleDepartment({ id: dept.id, name: dept.name })
              }
            />
          ))}
        </FolderGrid>
      )}
    </div>
  )
}
